// The review queue's own API surface — the CONTEXT the stand needs around a claim.
//
// The mutations still belong to `agentApi` (accept / reject / revert are the agent router's
// contract and this tab changes none of them). What lives here is everything the proposal row
// does not carry: what the record already says, what the click will write, how old the evidence
// is, and which gate the queue is holding up.
import { request } from './client.js';
import type { RunStats } from './agent.js';
import type { Problem } from '../../shared/problems.js';

/**
 * `extraction_runs.stats` carries two fields the shared `RunStats` interface omits — the JSON
 * has always had them, nothing ever read them. Declared here rather than widened on the shared
 * type so the Agent tab's own view of a run is untouched.
 *
 *   close_discipline  'docs' | 'register' — the UNIT the run is accountable for
 *   closing_account   what it says it did, per subject, including what it deliberately did NOT
 *                     propose. Parsed by src/shared/closingAccount.ts.
 */
export interface QueueRunStats extends RunStats {
  close_discipline?: string;
  closing_account?: string;
}

export interface Neighbour {
  of: 'requirement' | 'deliverable';
  ref: string;
  statement: string;
  dod: string | null;
  status: string;
  priority: string | null;
  type: string | null;
  sim: number;
}

export interface AcParent {
  ref: string;
  statement: string;
  status: string;
  priority: string;
  type: string;
  ac_count: number;
}

export interface ProposalsContext {
  engagement_id: number;
  next_refs: Record<string, number>;
  /** Digits per kind, straight from the allocator (server utils/refs.ts REF_PAD). */
  ref_pad: Record<string, number>;
  /** `null` means the register comparison DID NOT RUN. It is not an empty result. */
  neighbours: Record<string, Neighbour[]> | null;
  ac_parents: Record<string, AcParent>;
  docs: Record<string, { original_filename: string; effective_date: string | null }>;
  history: { total: number; held: number; amended: number; refused: number; unheld: number };
  refusals: Array<{ run_id: number | null; reason: string; n: number }>;
  gate: {
    gate: string;
    title: string;
    allMet: boolean;
    conditions: Array<{ label: string; met: boolean; detail: string }>;
  } | null;
  problems: Problem[];
  generated_at: string;
}

/**
 * The refusal vocabulary, in the order the reason panel offers it.
 *
 * `fresh` marks the two added with this tab. They exist because the reason is the ONLY signal
 * the run report ever gets back, and 20 of 27 historical refusals were filed as `duplicate` —
 * including claims that contradict a register row rather than repeat it.
 */
export const PROPOSAL_REJECT_REASONS = [
  { id: 'duplicate', label: 'Duplicate' },
  { id: 'contradicts_register', label: 'Contradicts the register', fresh: true },
  { id: 'superseded', label: 'Superseded by later evidence', fresh: true },
  { id: 'too_vague', label: 'Not testable' },
  { id: 'not_a_requirement', label: 'Not a requirement' },
  { id: 'wrong_evidence', label: 'Evidence does not support it' },
  { id: 'out_of_scope', label: 'Out of scope' },
  { id: 'other', label: 'Other' },
] as const;

export const REJECT_REASON_LABEL: Record<string, string> = Object.fromEntries(
  PROPOSAL_REJECT_REASONS.map((r) => [r.id, r.label]),
);

export const proposalsApi = {
  context: (engagementId: number) =>
    request<ProposalsContext>(`/proposals/context?engagement_id=${engagementId}`),

  /**
   * Walk back a REFUSAL. `POST /agent/proposals/:id/revert` is the accept's undo and answers 409
   * to anything else — correctly, because an accept created a row. A refusal created nothing, so
   * its undo is total, and the session ledger promises an undo on every row it shows.
   */
  unrefuse: (id: number) =>
    request<{ unrefused: boolean; proposal_id: number; note: string }>(
      `/proposals/${id}/unrefuse`, { method: 'POST' },
    ),
};

export default proposalsApi;
